/**
 * Full cart page (/cart) quantity stepper — Flutter parity (2026-08-08).
 *
 * Prestige's own LineItemQuantity custom element (theme.js) already does
 * a proper AJAX /cart/change.js update almost everywhere EXCEPT here — on
 * the full cart page specifically it deliberately does
 * `window.location.reload()` after a successful change (see
 * changeLineItemQuantity_fn's `if (pageType === 'cart') reload` branch).
 * That's intentional Prestige behavior, not a defect, but it's a hard
 * navigation flash on every +/- tap — Flutter's cart has no equivalent
 * interruption.
 *
 * Rather than edit theme.js (shared, would need re-patching on any future
 * Prestige sync), this intercepts the same two triggers LineItemQuantity
 * listens for — click on `[href*="/cart/change"]` and change on
 * `[data-line-key]` — at the capture phase on `document`, one tick before
 * LineItemQuantity's own bubble-phase listener would see them, stops
 * propagation so it never runs, and does its own /cart/change.js call
 * using the exact section-replace pattern CartDrawer already uses
 * elsewhere in this theme (request `sections=<main-cart section id>`,
 * swap that section's innerHTML with the response) — same end state as a
 * reload, no navigation.
 */
(function () {
  if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType !== 'cart') {
    return;
  }
  if (window.erosCartPageAjaxBound) return;
  window.erosCartPageAjaxBound = true;

  function sectionId(triggerEl) {
    // Prestige section wrapper id is always shopify-section-<id> on the
    // nearest .shopify-section ancestor — derived from the specific
    // line-item-quantity control the user actually interacted with, not a
    // blind document-wide first-match. The site-wide cart drawer (rendered
    // via overlay-group, before <main> in the DOM — layout/theme.liquid)
    // always has its own <line-item-quantity> too whenever the cart is
    // non-empty, and document order put the (hidden) drawer's instance
    // first — so `document.querySelector('line-item-quantity')` always
    // resolved to the drawer, never main-cart. The fetched response also
    // legitimately contains a #shopify-section-<drawerId> node (the drawer
    // section renders on every page), so applySectionHtml() "succeeded" —
    // just silently patched the closed drawer instead of the visible page.
    // Confirmed live 2026-08-09.
    var host = (triggerEl && triggerEl.closest('line-item-quantity')) || document.querySelector('line-item-quantity');
    var section = host ? host.closest('.shopify-section') : null;
    if (!section) return null;
    return section.id.replace('shopify-section-', '');
  }

  function dispatchCartChange(cart) {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart: cart },
      })
    );
  }

  function applySectionHtml(id, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('shopify-section-' + id);
    var current = document.getElementById('shopify-section-' + id);
    if (!fresh || !current) return false;
    current.innerHTML = fresh.innerHTML;
    return true;
  }

  function changeQuantity(lineKey, targetQuantity, triggerEl) {
    if (!lineKey || isNaN(targetQuantity)) return;
    var id = sectionId(triggerEl);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    var sectionsToBundle = id ? [id] : [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    fetch((window.Shopify && Shopify.routes && Shopify.routes.root) + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: targetQuantity, sections: sectionsToBundle.join(',') }),
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw json;
          return json;
        });
      })
      .then(function (cart) {
        var applied = id && cart.sections && cart.sections[id] ? applySectionHtml(id, cart.sections[id]) : false;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        if (!applied) {
          window.location.reload();
          return;
        }
        dispatchCartChange(cart);
      })
      .catch(function () {
        // Matches Prestige's own error path in spirit (rare — real network
        // failure or an inventory rejection) without reimplementing its
        // inline error markup: a reload still leaves the cart correct.
        window.location.reload();
      });
  }

  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('[href*="/cart/change"]');
      if (!link || !document.body.contains(link)) return;
      event.preventDefault();
      event.stopPropagation();
      var url = new URL(link.href, window.location.origin);
      changeQuantity(url.searchParams.get('id'), parseInt(url.searchParams.get('quantity'), 10), link);
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target.closest && event.target.closest('[data-line-key]');
      if (!input) return;
      event.stopPropagation();
      changeQuantity(input.getAttribute('data-line-key'), parseInt(input.value, 10), input);
    },
    true
  );
})();
